import React, { useEffect, useState } from 'react';
import { Modal, Box, List, ListItem, ListItemText } from '@mui/material';

import { Loading, ErrorMessage } from '@/src/ui';
import { apiRequest } from '@/src/services';

interface StandardQuestionsModalProps {
    open: boolean;
    onClose: () => void;
    standardId: number;
    standardCode: string;
}

// Lists the questions tied to a single standard (within the current report
// context). Owns its own fetch + loading/error state so the parent only needs
// to control visibility + identifier.
const StandardQuestionsModal: React.FC<StandardQuestionsModalProps> = ({
    open,
    onClose,
    standardId,
    standardCode,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [questions, setQuestions] = useState<{ questionId: number; title: string }[]>([]);

    useEffect(() => {
        if (!open || !standardId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            setQuestions([]);
            try {
                const { data } = await apiRequest<{ questionId: number; title: string }[]>(
                    'GET',
                    `reports/classStandardsReport/questions?standardId=${standardId}`
                );
                if (!cancelled) setQuestions(Array.isArray(data) ? data : []);
            } catch (e: any) {
                if (!cancelled) setError(e?.message || 'Failed to load questions.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, standardId]);

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    p: 3,
                    bgcolor: 'background.paper',
                    m: 'auto',
                    mt: 8,
                    width: 700,
                    maxWidth: '95%',
                    maxHeight: '80vh',
                    borderRadius: 2,
                    boxShadow: 24,
                    overflowY: 'auto',
                }}
            >
                <h2 style={{ marginTop: 0 }}>{standardCode} - Questions</h2>

                <Loading visible={loading} size="md" />
                <ErrorMessage error={error} />

                {!loading && !error && (
                    <List dense>
                        {questions.length === 0 ? (
                            <ListItem>
                                <ListItemText primary="No questions found for this standard in the selected assessment." />
                            </ListItem>
                        ) : (
                            questions.map((q) => (
                                <ListItem key={q.questionId}>
                                    <ListItemText primary={q.title || 'Untitled question'} />
                                </ListItem>
                            ))
                        )}
                    </List>
                )}
            </Box>
        </Modal>
    );
};

export default StandardQuestionsModal;
